import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Button } from '@ab-inbev-maz/compass';
import NavigationHeader from '../../components/navigation/NavigationHeader';
import TableGridView, { type GridCardItem } from '../../components/commos/TableGridView';
import CreateCanvasModal, { type NewCanvasConfig } from '../../components/canvas/CreateCanvasModal';
import Loading from '../../features/sso-auth/components/Loading';
import { useCanvasList } from '../../hooks/canvas/useCanvasList';
import { canvasService, type CanvasImage } from '../../services/canvasService';
import { useAlertStore } from '../../stores/alertStore';
import { createBlankCanvasImage } from '../../utils/blankCanvasImage';
import { toSecureImageUrl, toThumbImageUrl } from '../../utils/imageUrl';
import { toEditorState } from './canvasEditorState';

const DEFAULT_PAGE_SIZE = 25;
const BLANK_CANVAS_COLOR = '#FFFFFF';

const formatModifiedAt = (iso: string): string | undefined => {
  const relative = DateTime.fromISO(iso).setLocale('es').toRelative();
  return relative ? relative.charAt(0).toUpperCase() + relative.slice(1) : undefined;
};

const toGridItem = ({ id, data }: CanvasImage): GridCardItem => ({
  id,
  title: data.title,
  subtitle: formatModifiedAt(data.modified_at),
  thumbnailUrl: toSecureImageUrl(toThumbImageUrl(data.image)),
});

const MyCanvasPage = () => {
  const navigate = useNavigate();
  const showAlert = useAlertStore((state) => state.showAlert);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isOpening, setIsOpening] = useState(false);

  const [isDeleting, setIsDeleting] = useState(false);

  const { canvases, totalCount, isLoading, error, debouncedSearch, reload } = useCanvasList({
    page: currentPage,
    pageSize,
    search: searchValue,
  });
  const [hasLoaded, setHasLoaded] = useState(false);

  // A new search starts back on the first page.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!isLoading) setHasLoaded(true);
  }, [isLoading]);

  useEffect(() => {
    if (error) showAlert({ text: 'Error', message: error, status: 'error' });
  }, [error, showAlert]);

  const items = useMemo(() => canvases.map(toGridItem), [canvases]);

  const openCreateModal = () => {
    setCreateError(null);
    setIsCreateModalOpen(true);
  };

  // Saves a blank canvas (title, size and its white image) and opens it in the editor.
  const handleCreateCanvas = async (config: NewCanvasConfig) => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const image = await createBlankCanvasImage(config.width, config.height, BLANK_CANVAS_COLOR);
      const created = await canvasService.createCanvas({
        title: config.name,
        width: config.width,
        height: config.height,
        bg_color: BLANK_CANVAS_COLOR,
        image,
      });
      setIsCreateModalOpen(false);
      void navigate(`/canvas/${created.id}`, { state: toEditorState(created) });
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Error al crear el canvas');
    } finally {
      setIsCreating(false);
    }
  };

  // Opens a saved canvas with its latest data from the detail endpoint.
  const handleOpenCanvas = async (id: string | number) => {
    if (isOpening) return;
    setIsOpening(true);
    try {
      const canvas = await canvasService.getCanvas(String(id));
      void navigate(`/canvas/${canvas.id}`, { state: toEditorState(canvas) });
    } catch (err) {
      showAlert({
        text: 'Error',
        message: err instanceof Error ? err.message : 'Error al obtener el canvas',
        status: 'error',
      });
      setIsOpening(false);
    }
  };

  // Deletes each selected canvas and refreshes the list, stepping back a page when this one ends up empty.
  const handleDeleteCanvases = async (ids: (string | number)[]) => {
    if (ids.length === 0 || isDeleting) return;
    setIsDeleting(true);
    const results = await Promise.allSettled(ids.map((id) => canvasService.deleteCanvas(String(id))));
    const failed = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
    const deletedCount = ids.length - failed.length;

    if (failed.length === 0) {
      showAlert({
        text: ids.length === 1 ? 'Borrador eliminado' : 'Borradores eliminados',
        message: ids.length === 1 ? 'El borrador se eliminó correctamente.' : `Se eliminaron ${ids.length} borradores.`,
        status: 'success',
      });
    } else {
      const reason: unknown = failed[0].reason;
      showAlert({
        text: 'Error',
        message:
          deletedCount > 0
            ? `Se eliminaron ${deletedCount} de ${ids.length} borradores. ${reason instanceof Error ? reason.message : ''}`.trim()
            : reason instanceof Error
              ? reason.message
              : 'Error al eliminar el canvas',
        status: 'error',
      });
    }

    if (deletedCount > 0) {
      if (deletedCount >= canvases.length && currentPage > 1) setCurrentPage((page) => page - 1);
      else reload();
    }
    setIsDeleting(false);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  return (
    <>
      <NavigationHeader
        title="Mis canvas"
        description="Desde aquí puedes consultar todos los canvas creados y crear nuevos cuando lo necesites."
        backLink="/"
        actions={[
          <Button
            key="create-canvas"
            label="Crear nuevo canvas"
            rightIcon="functionalities/brush"
            variant="primary"
            onClick={openCreateModal}
            className="cursor-pointer"
            data-testid="action-button"
          />
        ]}
      />
      {!hasLoaded ? (
        <div className="flex items-center justify-center py-12">
          <Loading message="Esto solo va tomar un momento, estamos cargando los canvas." />
        </div>
      ) : (
        <TableGridView
          title="Borradores"
          datos={items}
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          showDeleteButton
          onOpenItem={(id) => void handleOpenCanvas(id)}
          onDelete={(ids) => void handleDeleteCanvases(ids)}
          deleteConfirmTitle="¿Estás seguro de eliminar este borrador?"
          deleteConfirmMessage="Estás a punto de eliminar este borrador. Una vez eliminado, no podrás recuperarlo ni acceder a él nuevamente."
          emptyMessage={debouncedSearch ? 'No se encontraron canvas.' : 'Aún no tienes canvas creados.'}
          emptyDescription={
            debouncedSearch ? 'Prueba con otro término de búsqueda.' : 'Crea tu primer canvas para empezar a diseñar combos y banners.'
          }
          emptyIcon="functionalities/brush"
        />
      )}
      {isDeleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70" data-testid="deleting-canvas">
          <Loading message="Eliminando..." />
        </div>
      )}
      {isOpening && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70" data-testid="opening-canvas">
          <Loading message="Abriendo el canvas..." />
        </div>
      )}
      {isCreateModalOpen && (
        <CreateCanvasModal
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={(config) => void handleCreateCanvas(config)}
          isSubmitting={isCreating}
          error={createError}
        />
      )}
    </>
  );
};

export default MyCanvasPage;
